import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col } from 'react-bootstrap';
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import Select from '../../../elements/Select';
import { Input } from '../../../elements/Input';
import CustomCheckbox from '../../../components/CustomCheckbox/CustomCheckbox.jsx';
import CustomRadio from '../../../components/CustomRadio/CustomRadio.jsx';
import GenericList from '../../../components/GenericList/GenericList.js';
import SwitchWidget from '../ControlPanel/SwitchWidget';
import RequiredStar from '../../../elements/RequiredStar';
import CustomMultiSelect from '../../../elements/CustomMultiSelect';

import deleteIcon from '../../../assets/img/deleting.svg';
import editIcon from '../../../assets/img/editing.svg';
import settingSlider from '../../../assets/img/settings-sliders.svg';
import '../../../styles/css/styles.css';
import '../../../components/Chat/ConversationSearch/ConversationSearch.css';

const FIXED_CATEGORIES = ['health', 'safety', 'security', 'other'];

class VisitorZoneRules extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showForm: false,
            editing: false,
            zones: [],
            pmOptions: [],
            filterZoneId: '',
            filterStatus: '',
            searchInput: '',
            appliedFilters: {},
            activeCount: 0,
            inactiveCount: 0,
        };
    }

    componentDidMount() {
        this.loadInitialData();
    }

    loadInitialData = async () => {
        await this.loadZones();
        await this.loadPmUsers();
    };

    loadPmUsers = async () => {
        const { userStore } = this.props;
        const data = await userStore.loadLookup('pm', '');
        const pmOptions = (data || []).map(u => ({ value: u.id, label: u.fullname }));
        this.setState({ pmOptions });
    };

    loadZones = async () => {
        const { visitorStore } = this.props;
        await visitorStore.loadZones();
        this.setState({ zones: visitorStore.zones });
    };

    getZoneOptions = () => {
        const { t } = this.props;
        const { zones } = this.state;
        return [
            { value: '', label: t('All Zones') },
            ...zones.map(z => ({ value: z.id, label: z.name })),
        ];
    };

    requestData = (params) => {
        const { visitorStore } = this.props;
        return visitorStore.loadSafetyRules(params).then(data => {
            this.setState({
                activeCount: data.activeCount || 0,
                inactiveCount: data.inactiveCount || 0,
            });
            return data;
        });
    };

    openAdd = () => {
        this.props.visitorStore.loadDefaultSafetyRule();
        this.setState({ showForm: true, editing: false });
    };

    openEdit = (rule) => {
        const { visitorStore } = this.props;
        visitorStore.currentSafetyRule = { ...rule };
        this.setState({ showForm: true, editing: true });
    };

    closeForm = () => {
        this.setState({ showForm: false });
    };

    handleSave = async () => {
        const { visitorStore, commonStore, t } = this.props;
        const rule = visitorStore.currentSafetyRule;

        if (!rule.rule_name || !rule.rule_name.trim()) {
            commonStore.addNotification(t('Please enter rule name'), null, 'error');
            return;
        }
        if (!rule.question || !rule.question.trim()) {
            commonStore.addNotification(t('Please enter question'), null, 'error');
            return;
        }

        try {
            await visitorStore.saveSafetyRule({
                ...rule,
                zone_id: rule.zone_id || null,
                apply_to_all: !!rule.trigger_alert,
            });
            commonStore.addNotification(t('Rule Saved'), null, 'success');
            this.setState({ showForm: false });
            visitorStore.resetSafetyRulesLastLoadTime();
        } catch (e) {
            commonStore.addNotification(t(e.message || 'Error saving rule'), null, 'error');
        }
    };

    handleDelete = (id) => {
        const { commonStore, t, visitorStore } = this.props;
        return this.props.handleDelete(id, async (id) => {
            await visitorStore.deleteSafetyRule(id);
            commonStore.addNotification(t('Rule Deleted'), null, 'error');
            visitorStore.resetSafetyRulesLastLoadTime();
        });
    };

    handleToggleActive = async (rule) => {
        const { visitorStore, commonStore, t } = this.props;
        try {
            await visitorStore.saveSafetyRule({
                ...rule,
                status: rule.status === 'active' ? 'inactive' : 'active',
            });
            commonStore.addNotification(t('Updated'), null, 'success');
            visitorStore.resetSafetyRulesLastLoadTime();
        } catch (e) {
            commonStore.addNotification(t('Error'), null, 'error');
        }
    };

    getCategoryOptions = () => {
        const { t } = this.props;
        return FIXED_CATEGORIES.map(c => ({ value: c, label: t(c.charAt(0).toUpperCase() + c.slice(1)) }));
    };

    getCategoryBadge = (category) => {
        const cat = (category || 'health').toLowerCase();
        const colorMap = {
            health: '#ef4444',
            safety: '#f59e0b',
            security: '#2769f6',
            other: '#10b981',
        };
        const color = colorMap[cat] || '#6b7280';
        return (
            <span style={{
                display: 'inline-block',
                width: 'fit-content',
                padding: '2px 14px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600,
                background: color + '1a',
                color,
                border: `1px solid ${color}`,
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
            }}>
                {cat}
            </span>
        );
    };

    buildFilters = ({ status, zone_id, name } = {}) => {
        const { filterStatus, filterZoneId, searchInput } = this.state;
        const s = status !== undefined ? status : filterStatus;
        const z = zone_id !== undefined ? zone_id : filterZoneId;
        const n = name !== undefined ? name : searchInput;
        const filters = {};
        if (s) filters.status = s;
        if (z) filters.zone_id = z;
        if (n) filters.name = n;
        return filters;
    };

    applySearch = () => {
        const { searchInput } = this.state;
        this.setState({ appliedFilters: this.buildFilters({ name: searchInput }) });
    };

    setStatusFilter = (status) => {
        const { filterStatus } = this.state;
        const newStatus = filterStatus === status ? '' : status;
        this.setState({
            filterStatus: newStatus,
            appliedFilters: this.buildFilters({ status: newStatus }),
        });
    };

    setZoneFilter = (zoneId) => {
        this.setState({
            filterZoneId: zoneId,
            appliedFilters: this.buildFilters({ zone_id: zoneId }),
        });
    };

    getColumns = () => {
        const { t } = this.props;
        return [
            {
                Header: 'Rule Name',
                accessor: 'rule_name',
                isSortable: true,
                Cell: ({ row }) => (
                    <span className="font-weight-bold">{row.original.rule_name}</span>
                ),
            },
            {
                Header: 'Question',
                accessor: 'question',
                isSortable: true,
                Cell: ({ row }) => (
                    <span className="text-muted" style={{ maxWidth: 220, display: 'block' }}>
                        {row.original.question}
                    </span>
                ),
            },
            {
                Header: 'Zone',
                id: 'zone_name',
                accessor: row => row.zone_name || '',
                isSortable: false,
                Cell: ({ row }) => row.original.zone_name
                    ? row.original.zone_name
                    : <span className="text-muted">{t('All Zones')}</span>,
            },
            {
                Header: 'Category',
                accessor: 'category',
                isSortable: true,
                Cell: ({ row }) => this.getCategoryBadge(row.original.category),
            },
            {
                Header: 'Active',
                id: 'status_toggle',
                accessor: row => row.status,
                isSortable: false,
                Cell: ({ row }) => (
                    <SwitchWidget
                        value={row.original.status === 'active'}
                        onChange={() => this.handleToggleActive(row.original)}
                    />
                ),
            },
            {
                Header: 'Action',
                id: 'actions',
                accessor: row => row.id,
                isSortable: false,
                Cell: ({ row }) => (
                    <div className="actions-center justify-content-center">
                        <Button icon_sm fill onClick={() => this.openEdit(row.original)}>
                            <img src={editIcon} alt="edit" />
                        </Button>
                        <Button icon_sm_delete fill onClick={() => this.handleDelete(row.original.id)}>
                            <img src={deleteIcon} alt="delete" />
                        </Button>
                    </div>
                ),
            },
        ];
    };

    renderForm() {
        const { t, visitorStore } = this.props;
        const { editing, pmOptions } = this.state;
        const rule = visitorStore.currentSafetyRule;
        const zoneOptions = this.getZoneOptions();
        const zoneSelectOpts = zoneOptions.filter(o => o.value !== '');
        const zoneSelectWithNull = [{ value: null, label: t('All Zones (Global)') }, ...zoneSelectOpts];
        const categoryOptions = this.getCategoryOptions();

        return (
            <div className="table-list">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0 font-weight-bold">
                        {editing ? t('Edit Rule') : t('Add New Rule')}
                    </h4>
                    <Button onClick={this.closeForm}>
                        {t('Go Back')}
                    </Button>
                </div>

                <div className="row">
                <div className="col-md-8">

                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group field field-string w-100">
                            <label className="control-label fs15">{t('Rule Name')}<RequiredStar /></label>
                            <Input
                                value={rule.rule_name || ''}
                                onChange={e => visitorStore.handleSafetyRuleChange('rule_name', e.target.value)}
                                placeholder={t('e.g. Daily Temperature Check')}
                                maxLength={100}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group field field-string w-100">
                            <label className="control-label fs15">{t('Zone')}</label>
                            <Select
                                options={zoneSelectWithNull}
                                value={zoneSelectWithNull.find(o => o.value === rule.zone_id) || zoneSelectWithNull[0]}
                                onChange={opt => visitorStore.handleSafetyRuleChange('zone_id', opt ? opt.value : null)}
                                placeholder={t('All Zones (Global)')}
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <div className="form-group field field-string w-100">
                            <label className="control-label fs15">{t('Visitor Question')}<RequiredStar /></label>
                            <textarea
                                className="form-control"
                                rows={3}
                                style={{ height: 'auto', minHeight: 72 }}
                                maxLength={500}
                                value={rule.question || ''}
                                onChange={e => visitorStore.handleSafetyRuleChange('question', e.target.value)}
                                placeholder={t('e.g. Do you have any symptoms of illness?')}
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group field field-string w-100">
                            <label className="control-label fs15">{t('Category')}</label>
                            <Select
                                options={categoryOptions}
                                value={categoryOptions.find(o => o.value === (rule.category || 'health'))}
                                onChange={opt => visitorStore.handleSafetyRuleChange('category', opt ? opt.value : 'health')}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group field field-string w-100">
                            <label className="control-label fs15">{t('Question Type')}</label>
                            <Input value={t('Yes / No')} disabled className="text-center" />
                        </div>
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-md-12">
                        <div className="form-group field field-string w-100 mb-3">
                            <CustomCheckbox
                                inline
                                label={t('Is Mandatory')}
                                isChecked={!!rule.is_mandatory}
                                onChange={val => visitorStore.handleSafetyRuleChange('is_mandatory', val)}
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <div className="form-group field field-string w-100 mb-3">
                            <CustomCheckbox
                                inline
                                label={t('Trigger Alert on YES')}
                                isChecked={!!rule.trigger_alert}
                                onChange={val => {
                                    visitorStore.handleSafetyRuleChange('trigger_alert', val);
                                    if (val && !rule.required_authority) {
                                        const existing = visitorStore.safetyRules.find(
                                            r => r.trigger_alert && r.required_authority
                                        );
                                        if (existing) {
                                            visitorStore.handleSafetyRuleChange('required_authority', existing.required_authority);
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                {rule.trigger_alert && (
                    <div className="row mb-2">
                        <div className="col-md-8">
                            <div style={{ background: '#fff8f0', border: '1px solid #f59e0b', borderRadius: 8, padding: '14px 16px', overflow: 'visible' }}>
                                <div className="d-flex align-items-center mb-3">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginRight: 12 }}>
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                        <line x1="12" y1="9" x2="12" y2="13"/>
                                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                                    </svg>
                                    <span style={{ color: '#b45309', fontWeight: 500, fontSize: 13 }}>
                                        {t('Alert triggers requires authority approval.')}
                                    </span>
                                </div>
                                <div className="form-group field field-string w-100 mb-0">
                                    <label className="control-label fs15" style={{ color: '#b45309' }}>{t('Filter employees by title')}</label>
                                    <CustomMultiSelect
                                        options={pmOptions}
                                        value={rule.required_authority || ''}
                                        onChange={ids => {
                                            const val = ids.join(',');
                                            visitorStore.handleSafetyRuleChange('required_authority', val);
                                            visitorStore.safetyRules.forEach(r => {
                                                if (r.id !== rule.id && r.trigger_alert) {
                                                    r.required_authority = val;
                                                }
                                            });
                                        }}
                                        hasSelectAll={false}
                                        customOptions
                                        className="custom-multi-select-max-height-150"
                                        overrideStrings={{
                                            selectSomeItems: t('Select employees'),
                                            allItemsAreSelected: t('All employees selected'),
                                            search: t('Search'),
                                            selectAll: t('Select All'),
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group field field-string w-100">
                            <label className="control-label fs15">{t('Status')}</label>
                            <div className="task-statuses">
                                {['active', 'inactive'].map(s => (
                                    <CustomRadio
                                        key={s}
                                        name="rule_status"
                                        option={s}
                                        label={t(s.charAt(0).toUpperCase() + s.slice(1))}
                                        checked={(rule.status || 'active') === s}
                                        onChange={() => visitorStore.handleSafetyRuleChange('status', s)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-12">
                        <Button fill wd onClick={this.handleSave}>
                            {t('Save Rule')}
                        </Button>
                        <Button onClick={this.closeForm} style={{ marginLeft: 24 }}>
                            {t('Cancel')}
                        </Button>
                    </div>
                </div>

                </div>
                </div>
            </div>
        );
    }

    render() {
        const { t, visitorStore } = this.props;
        const { showForm, filterZoneId, searchInput,
            appliedFilters, activeCount, inactiveCount } = this.state;
        const zoneOptions = this.getZoneOptions();

        if (showForm) {
            return this.renderForm();
        }

        const totalCount = activeCount + inactiveCount;

        const statusTabs = (
            <Row className="align-content-center mt-2 gx-0">
                <Col xs={12} lg={7}>
                    <Row className="member_status__summary">
                        <Col xs={4} className="mt-2 mt-sm-0 justify-content-center justify-content-sm-start justify-content-lg-center">
                            <div
                                className="highlightedgreen"
                                role="button"
                                onClick={() => this.setStatusFilter('active')}
                            >
                                {t('Active rules')}: {activeCount}
                            </div>
                        </Col>
                        <Col xs={4} className="mt-2 mt-sm-0 justify-content-center">
                            <div
                                className="highlightedred"
                                role="button"
                                onClick={() => this.setStatusFilter('inactive')}
                            >
                                {t('Inactive rules')}: {inactiveCount}
                            </div>
                        </Col>
                        <Col xs={4} className="mt-2 mt-sm-0 justify-content-center justify-content-sm-end justify-content-lg-center">
                            <div
                                className="highlighted"
                                role="button"
                                onClick={() => this.setState(
                                    { filterStatus: '' },
                                    () => this.setState({ appliedFilters: this.buildFilters({ status: '' }) })
                                )}
                            >
                                {t('All rules')}: {totalCount}
                            </div>
                        </Col>
                    </Row>
                </Col>
                <Col xs={12} lg={5} className="mt-2 mt-lg-0 text-start text-lg-end">
                    <Row className="gx-0 genericlist_header_xxl_auto justify-content-lg-end">
                        <Col xs={4} xxl={3} className="text-end">
                            <Button fill onClick={this.openAdd}>
                                <div className="btn-container">
                                    <i className="fa fa-plus-circle fa-xl me-2" />
                                    <span>{t('Add New Rule')}</span>
                                </div>
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
        );

        const searchRow = (
            <Row className="mb-4 mt-1">
                <Col xs={{ span: 9, order: 2 }} sm={{ span: 10, order: 2 }} md={{ span: 9, order: 1 }} className="mt-2">
                    <div className="conversation-search">
                        <input
                            type="text"
                            className="conversation-search-input"
                            placeholder={t('Search')}
                            value={searchInput}
                            onChange={e => this.setState({ searchInput: e.target.value })}
                            onKeyDown={e => e.key === 'Enter' && this.applySearch()}
                        />
                        <i className="fa fa-search conversation-search-icon" onClick={this.applySearch} />
                    </div>
                </Col>
                <Col xs={{ span: 12, order: 1 }} sm={{ span: 6, order: 1 }} md={{ span: 2, order: 1 }} className="mt-2">
                    <Select
                        options={zoneOptions}
                        value={zoneOptions.find(o => o.value === filterZoneId) || zoneOptions[0]}
                        onChange={opt => this.setZoneFilter(opt ? opt.value : '')}
                        placeholder={t('All Zones')}
                    />
                </Col>
                <Col xs={{ span: 3, order: 3 }} sm={{ span: 2, order: 3 }} md={{ span: 1, order: 1 }} className="mt-2 gx-0 text-center">
                    <Button icon fill onClick={this.applySearch}>
                        <img src={settingSlider} alt="filter" style={{ height: '1rem' }} />
                    </Button>
                </Col>
            </Row>
        );

        return (
            <div className="table-list">
                <GenericList
                    columns={this.getColumns()}
                    requestData={this.requestData}
                    filters={appliedFilters}
                    lastListLoadTime={visitorStore.safetyRulesLastLoadTime}
                    tableHeader={t('Rule Configuration')}
                    splitHeader
                    header={searchRow}
                >
                    {statusTabs}
                </GenericList>
            </div>
        );
    }
}

export default inject('visitorStore', 'userStore', 'commonStore')(
    applicationRouter(withLocalization(withRemoveDialog(observer(VisitorZoneRules))))
);
